import { Company } from '../types/company.type';
import { LogoNode, LogoFile, EnhancedLogoNode } from '../types/logo.type';
import { environment } from '../../../environments/environment';

/**
 * Extracts and attaches logo URLs to companies from enhanced logo nodes
 * @param companies - Array of companies to update
 * @param enhancedLogos - Array of enhanced logo nodes with populated file data
 * @param baseUrl - Base URL to prepend to relative logo URLs (optional, defaults to environment.baseUrl)
 * @returns Companies array with logoUrl field populated where available
 */
export function attachLogosToCompanies(
  companies: Company[],
  enhancedLogos: EnhancedLogoNode[],
  baseUrl?: string,
): Company[] {
  if (!enhancedLogos || !Array.isArray(enhancedLogos)) {
    return companies;
  }
  const urlBase = baseUrl || environment.baseUrl;

  // Ensure we don't have duplicate companies in input
  const uniqueCompanies = companies.filter(
    (company, index, self) =>
      index === self.findIndex((c) => c.CLIEN_NM === company.CLIEN_NM),
  );

  if (uniqueCompanies.length !== companies.length) {
    console.warn(
      '⚠️ Detected duplicate companies in input, filtering to unique ones:',
      {
        original: companies.length,
        unique: uniqueCompanies.length,
        duplicates: companies.length - uniqueCompanies.length,
      },
    );
  }

  const result = uniqueCompanies.map((company) => {
    // Find ALL logo nodes for this company
    const companyLogos = enhancedLogos.filter(
      (item) => item.attributes?.field_empresa === company.CLIEN_NM,
    );

    if (companyLogos.length === 0) {
      return company;
    }

    // Find the most recent logo based on 'changed' date
    const mostRecentLogo = companyLogos.reduce((latest, current) => {
      const latestDate = new Date(latest.attributes?.changed || 0);
      const currentDate = new Date(current.attributes?.changed || 0);
      return currentDate > latestDate ? current : latest;
    });

    if (!mostRecentLogo?.populatedFile) {
      console.warn(
        `⚠️ Company ${company.CLIEN_NM} most recent logo has no populated file:`,
        {
          logoId: mostRecentLogo?.id,
          expectedFileId: mostRecentLogo?.relationships?.field_imagen?.data?.id,
          hasRelationship: !!mostRecentLogo?.relationships?.field_imagen?.data,
          populatedFile: !!mostRecentLogo?.populatedFile,
        },
      );
      return company;
    }

    // Validate file association
    const expectedFileId = mostRecentLogo.relationships?.field_imagen?.data?.id;
    const actualFileId = mostRecentLogo.populatedFile.id;

    if (expectedFileId !== actualFileId) {
      console.error(`🚨 File ID mismatch for company ${company.CLIEN_NM}:`, {
        logoId: mostRecentLogo.id,
        expectedFileId,
        actualFileId,
        fileName: mostRecentLogo.populatedFile.attributes?.filename,
      });
    }

    // Extract the URL from the populated file and prepend base URL
    const relativeUrl = mostRecentLogo.populatedFile.attributes?.uri?.url;
    const logoUrl = relativeUrl ? `${urlBase}${relativeUrl}` : undefined;

    return {
      ...company,
      logoUrl: logoUrl,
    };
  });

  // Final safety check - ensure no duplicates in result
  const finalResult = result.filter(
    (company, index, self) =>
      index === self.findIndex((c) => c.CLIEN_NM === company.CLIEN_NM),
  );

  if (finalResult.length !== result.length) {
    console.error('🚨 Found duplicates in result, filtering them out:', {
      beforeFilter: result.length,
      afterFilter: finalResult.length,
    });
  }

  return finalResult;
}

/**
 * Finds logo URL for a specific company from enhanced logo nodes
 * @param companyId - Company CLIEN_NM to search for
 * @param enhancedLogos - Array of enhanced logo nodes with populated file data
 * @param baseUrl - Base URL to prepend to relative logo URLs (optional, defaults to environment.baseUrl)
 * @returns Logo URL string or undefined if not found
 */
export function findCompanyLogoUrl(
  companyId: string,
  enhancedLogos: EnhancedLogoNode[],
  baseUrl?: string,
): string | undefined {
  if (!enhancedLogos || !Array.isArray(enhancedLogos)) {
    return undefined;
  }

  const urlBase = baseUrl || environment.baseUrl;

  // Find ALL logo nodes for this company
  const companyLogos = enhancedLogos.filter(
    (item) => item.attributes?.field_empresa === companyId,
  );

  if (companyLogos.length === 0) {
    return undefined;
  }

  // Find the most recent logo based on 'changed' date
  const mostRecentLogo = companyLogos.reduce((latest, current) => {
    const latestDate = new Date(latest.attributes?.changed || 0);
    const currentDate = new Date(current.attributes?.changed || 0);
    return currentDate > latestDate ? current : latest;
  });

  if (!mostRecentLogo?.populatedFile) {
    return undefined;
  }

  // Extract the URL from the populated file and prepend base URL
  const relativeUrl = mostRecentLogo.populatedFile.attributes?.uri?.url;
  return relativeUrl ? `${urlBase}${relativeUrl}` : undefined;
}

/**
 * Legacy function for backward compatibility - converts enhanced logos to old format
 * @param enhancedLogos - Array of enhanced logo nodes
 * @returns Combined array of logo nodes and files for legacy code
 */
export function convertToLegacyFormat(
  enhancedLogos: EnhancedLogoNode[],
): (LogoNode | LogoFile)[] {
  const result: (LogoNode | LogoFile)[] = [...enhancedLogos];

  enhancedLogos.forEach((logo) => {
    if (logo.populatedFile) {
      result.push(logo.populatedFile);
    }
  });

  return result;
}
